import Dialog from "../../dialog/Dialog";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  STATUS_DIALOG_WIDTH,
  StatusApprovalSection,
  StatusSettingsBody,
} from "../../common/StatusSettingsParts";
import { approveOrRejectRental } from "./rentalService";

function toApprovalTriState(v) {
  if (v === true || v === 1 || v === "1") return 1;
  if (v === 2 || v === "2") return 2;
  return 0;
}

const RentalApprovalSettings = ({ open, onClose, rentalData, onNotify }) => {
  const queryClient = useQueryClient();
  const [isApproved, setIsApproved] = useState(0);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    if (!open || !rentalData) return;
    setIsApproved(toApprovalTriState(rentalData.is_approved));
    setRejectionReason("");
  }, [open, rentalData]);

  const mutation = useMutation({
    mutationFn: () =>
      approveOrRejectRental({
        rental_item_id: rentalData.id,
        is_approved: isApproved,
        rejection_reason: isApproved === 2 ? rejectionReason.trim() || undefined : undefined,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(["rentals-admin"]);
      onNotify?.({
        type: "success",
        message: data?.message || "Rental updated.",
      });
      onClose();
    },
    onError: (error) => {
      onNotify?.({
        type: "error",
        message: error?.response?.data?.message || error?.message || "Request failed.",
      });
    },
  });

  const handleSave = () => {
    if (isApproved !== 1 && isApproved !== 2) {
      onNotify?.({
        type: "error",
        message: "Choose Approved or Rejected to update this rental.",
      });
      return;
    }
    const prev = toApprovalTriState(rentalData?.is_approved);
    if (prev === isApproved) {
      onClose();
      return;
    }
    mutation.mutate();
  };

  if (!rentalData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Rental Status"
      width={STATUS_DIALOG_WIDTH}
      primaryButtonText="Save Changes"
      primaryButtonAction={handleSave}
      isLoading={mutation.isPending}
    >
      <StatusSettingsBody>
        <StatusApprovalSection
          value={isApproved}
          onChange={setIsApproved}
          description="Approve / reject this rental"
        />

        {isApproved === 2 && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Rejection reason (optional)
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Shown to the supplier when helpful"
            />
          </div>
        )}
      </StatusSettingsBody>
    </Dialog>
  );
};

export default RentalApprovalSettings;
